import {
  AuthChannelType,
  ValidatedUserIdentifierType,
} from "@/generated/graphql";
import { useAppStore } from "@/stores/app.store";
import { useAuthStore } from "@/stores/auth/auth.store";
import { computed, watchEffect } from "vue";
import * as z from "zod/v4";
import {
  ChannelEventType,
  useChannelEvents,
} from "@/composables/channel-events";
import {
  EMBED_MESSAGE_EVENT_NAME,
  EmbedMessageType,
} from "@f3/shared/embed-messages";

const authenticationEmbedMessageSchema = z.object({
  type: z.literal(EmbedMessageType.Authentication),
  jwt: z.string().nullable(),
  matches_identity: z.boolean(),
  validated_identifiers: z.array(
    z.object({
      type: z.enum(ValidatedUserIdentifierType),
      value: z.string(),
      confirmation_token: z.string(),
    }),
  ),
  auth_channel: z.enum(AuthChannelType).nullable(),
  integration_mode: z.string().nullable(),
});

const logoutEmbedMessageSchema = z.object({
  type: z.literal(EmbedMessageType.Logout),
});

const sdkAuthUserSchema = z.object({
  id: z.string(),
  username: z.string(),
  email: z.string(),
});

const authenticationFinishedEmbedMessageSchema = z.object({
  type: z.literal(EmbedMessageType.AuthenticationFinished),
  user: sdkAuthUserSchema,
});

const modalStateChangedEmbedMessageSchema = z.object({
  type: z.literal(EmbedMessageType.ModalStateChanged),
  isOpen: z.boolean(),
});

const displayModeChangedEmbedMessageSchema = z.object({
  type: z.literal(EmbedMessageType.DisplayModeChanged),
  mode: z.enum(["auto-height", "viewport-locked"]),
});

const embedReadyMessageSchema = z.object({
  type: z.literal(EmbedMessageType.EmbedReady),
});

const embedMessageSchema = z.object({
  type: z.literal(EMBED_MESSAGE_EVENT_NAME),
  data: z.union([
    authenticationEmbedMessageSchema,
    embedReadyMessageSchema,
    authenticationFinishedEmbedMessageSchema,
    logoutEmbedMessageSchema,
    modalStateChangedEmbedMessageSchema,
    displayModeChangedEmbedMessageSchema,
  ]),
});

type EmbedMessage = z.infer<typeof embedMessageSchema>;

// Module-level so sendEmbedLogout can access it without being inside the composable
let sdkOrigin: string | null = null;

/**
 * Returns the origin of the parent SDK discovered during the embed handshake,
 * or `null` if no message from the SDK has been received yet. Consumed by
 * `useAuthModalPostMessage` to target `auth-modal:success` messages at the
 * parent window when the frontend is running in an embed iframe.
 */
export function getSdkOrigin(): string | null {
  return sdkOrigin;
}

export function useEmbedAuthenticationBroker() {
  const appStore = useAppStore();
  const authStore = useAuthStore();
  const { emitChannelEvent, watchEventsEffect } = useChannelEvents();

  function sendMessage(message: EmbedMessage["data"]) {
    if (!sdkOrigin) {
      console.error(
        "[EmbedBroker] SDK origin not yet discovered, cannot send message",
      );
      return;
    }
    window.parent.postMessage(
      {
        type: EMBED_MESSAGE_EVENT_NAME,
        data: message,
      },
      sdkOrigin,
    );
  }

  const isReady = computed(() => appStore.isEmbed && authStore.initialized);

  watchEventsEffect((event) => {
    if (event.type === ChannelEventType.USER_IDENTIFIED) {
      console.log(
        "[UseEmbedAuthenticationBroker] Sending authentication finished message via embed",
      );
      sendMessage({
        type: EmbedMessageType.AuthenticationFinished,
        user: {
          id: event.user.id,
          username: event.user.username,
          email: event.user.email,
        },
      });
    }
  });

  watchEffect((onCleanup) => {
    if (!isReady.value) return;

    const listener = (event: MessageEvent) => {
      if (event.data.type !== EMBED_MESSAGE_EVENT_NAME) return;

      // Reject messages from unexpected origins once SDK origin is known
      if (sdkOrigin !== null && event.origin !== sdkOrigin) return;

      const result = embedMessageSchema.safeParse(event.data);

      if (!result.success) {
        console.warn(`[EmbedAuthenticationBroker] Invalid message received`, {
          event,
          message: result,
        });
        return;
      }

      // Capture origin from first valid SDK message
      if (sdkOrigin === null) {
        sdkOrigin = event.origin;
      }

      const { data } = result.data;

      switch (data.type) {
        case EmbedMessageType.Authentication:
          appStore.setIntegrationMode(data.integration_mode);

          if (typeof data.jwt === "string" && data.auth_channel) {
            console.log(
              "[UseEmbedAuthenticationBroker] Authenticating via embed",
              data,
            );
            authStore.onSessionSet({
              jwt: data.jwt,
              matchesIdentity: data.matches_identity,
              authChannelType: data.auth_channel,
            });
            if (!data.matches_identity) {
              console.log(
                "[UseEmbedAuthenticationBroker] Signing up with identifiers via embed",
              );
              emitChannelEvent({
                type: ChannelEventType.SIGN_UP_WITH_IDENTIFIERS,
                validatedIdentifiers: data.validated_identifiers,
                authChannelType: data.auth_channel,
              });
            }
          } else {
            console.log("[UseEmbedAuthenticationBroker] Logging out via embed");
            authStore.logout();
          }
          break;
        default:
          console.warn(`[EmbedAuthenticationBroker] Unknown message type`, {
            event,
            message: result,
          });
          break;
      }
    };

    window.addEventListener("message", listener);

    // EmbedReady is a non-sensitive handshake signal — safe to send with "*"
    // because sdkOrigin hasn't been discovered yet (deadlock: SDK won't send
    // until it receives EmbedReady, but we can't send without knowing the origin).
    // All subsequent messages containing auth data use the discovered sdkOrigin.
    window.parent.postMessage(
      {
        type: EMBED_MESSAGE_EVENT_NAME,
        data: { type: EmbedMessageType.EmbedReady },
      },
      "*",
    );

    onCleanup(() => {
      window.removeEventListener("message", listener);
    });
  });
}

export function sendEmbedLogout() {
  if (!sdkOrigin) {
    console.error(
      "[EmbedBroker] Cannot send logout: SDK origin not discovered",
    );
    return;
  }
  window.parent.postMessage(
    {
      type: EMBED_MESSAGE_EVENT_NAME,
      data: { type: EmbedMessageType.Logout },
    },
    sdkOrigin,
  );
}

/**
 * Notify the host SDK that a modal opened or closed inside the embed
 * iframe. The SDK uses this to lock host-page scrolling, lock the
 * iframe to viewport height (so `position: fixed` modals centre
 * correctly), and scroll the iframe into view.
 */
export function sendEmbedModalState(isOpen: boolean) {
  if (!sdkOrigin) return; // silently skip — no handshake yet
  window.parent.postMessage(
    {
      type: EMBED_MESSAGE_EVENT_NAME,
      data: { type: EmbedMessageType.ModalStateChanged, isOpen },
    },
    sdkOrigin,
  );
}

/**
 * Request the host SDK to switch the embed iframe's display mode.
 * - `auto-height`: the iframe-resizer controls the height (default)
 * - `viewport-locked`: the iframe is locked to the host viewport height
 *   (used by show pages that need to fill the screen)
 */
export function sendEmbedDisplayMode(mode: "auto-height" | "viewport-locked") {
  if (!sdkOrigin) return;
  window.parent.postMessage(
    {
      type: EMBED_MESSAGE_EVENT_NAME,
      data: { type: EmbedMessageType.DisplayModeChanged, mode },
    },
    sdkOrigin,
  );
}
